/**
 * ⚠️ Notice about `callbackUrl`
 *
 * `callbackUrl` is only a **URL** to navigate to after login.
 * It does NOT restore any in-memory state (Redux store, React `location.state`,
 * component state, wizard progress, etc.).
 *
 * This means: after redirecting to login (401/451) and coming back via `callbackUrl`,
 * the destination page will load in a “cold start” scenario (like a fresh tab/refresh).
 *
 * ✅ To avoid broken screens:
 * - Destination routes should be able to render using **URL params + API fetch** (recommended)
 * - If you need UI context (tab, filters, etc.), persist it via query params or storage
 * - Do NOT depend on Redux data that only exists because the user visited another page first
 *
 * ⚠️ Potential breaking behavior:
 * If the target page requires Redux/location.state from a previous page, it may break
 * when opened after login redirect or in a new tab/refresh.
 */
import { ROUTES } from '@router/constants.ts';

const CALLBACK_URL = 'callbackUrl';

export const getCallbackUrl = (params: URLSearchParams): string | null => params.get(CALLBACK_URL);

export const addCallbackUrl = (url: string, window: Window): string => {
    const paramChar = url.includes('?') ? `&` : '?';
    return `${url}${paramChar}${CALLBACK_URL}=${window.location.pathname.replace('/', '')}`;
};

export const redirectToLoginWithCallbackUrl = () => {
    const loginPage = addCallbackUrl(`/${ROUTES.AUTH.LOGIN}`, window);
    window.location.replace(loginPage);
};

export const redirectToServerErrorWithCallbackUrl = (): void => {
    const url = addCallbackUrl(`/${ROUTES.DEFAULT.SERVER_ERROR}`, window);
    window.location.replace(url);
};

export const openInNewTab = (url: string): Window | null => window.open(url, '_blank', 'noopener,noreferrer');
